import { machineTrackingService } from '@/api/hub'
import {
  MachineStatus,
  MachineTrackingModel,
  MachineTrackingModelSchema,
} from '@/api/hub/machineTracking/list.schema'
import { fetchMachineSelectOptions } from '@/api/options/machine'
import {
  MachineSelectOption,
  MachineSelectOptions,
} from '@/api/options/machine.schema'
import { QueryClient } from '@tanstack/react-query'
import {
  action,
  computed,
  flow,
  makeObservable,
  observable,
  runInAction,
} from 'mobx'

export type MachineState = Pick<
  MachineSelectOption,
  'code' | 'group' | 'capacityPerHour'
> &
  MachineTrackingModel & {
    isLoading?: boolean
    durationInMinutes: number
    formattedDuration: string
  }

// MachineTracking store class
export default class MachineTrackingStore {
  trackingService
  queryClient
  // Map of machineid and machine tracking instances
  machines = new Map<number, MachineState>()
  error = null
  private _handlersRegistered = false
  private _durationInterval: ReturnType<typeof setInterval> | null = null

  constructor(
    trackingService: typeof machineTrackingService,
    queryClient: QueryClient
  ) {
    makeObservable(this, {
      machines: observable,
      error: observable,
      machineList: computed,
      loadAllMachineTrackingStates: action,
      updateMachineTrackingState: action,
      startJob: flow,
      completeJob: flow,
      offService: flow,
      returnService: flow,
      fetchMachines: action,
      setMachineIsLoading: action,
      updateDurations: action,
    })
    this.trackingService = trackingService
    this.queryClient = queryClient

    const MAX_RETRIES = 3
    let retryCount = 0
    const tryRequestTrackingData = () => {
      const allMachines = this.getAllMachines()
      if (allMachines && allMachines.length > 0) {
        this.trackingService.requestMachineTrackingData()
      } else if (retryCount < MAX_RETRIES) {
        retryCount++
        setTimeout(tryRequestTrackingData, 1000)
      } else {
        console.warn('Machine options not available after maximum retries.')
      }
    }
    this.trackingService.startHubConnection(() => {
      tryRequestTrackingData()
    })
    this.registerEventHandlers()
    this._startDurationInterval()
  }

  private _startDurationInterval() {
    if (this._durationInterval) return
    this._durationInterval = setInterval(() => {
      this.updateDurations()
    }, 60000)
  }

  // Optionally, add a cleanup method if you ever dispose the store
  dispose() {
    if (this._durationInterval) {
      clearInterval(this._durationInterval)
      this._durationInterval = null
    }
  }

  get machineList() {
    return Array.from(this.machines.values())
  }

  // Get the machine state by code
  // This function returns a computed observable
  machineStateByCode = (code: string) => {
    return computed(() => {
      const machine = Array.from(this.machines.values()).find(
        (m) => m.code === code
      )
      if (!machine) {
        // skip, wait until all machine are loaded after SignalR connection started
        return null
      }
      return machine
    }).get()
  }

  isRunningThisLotNo = (lotNo: string) => {
    return computed(() => {
      const machine = Array.from(this.machines.values()).find(
        (m) => m.lotNo === lotNo
      )
      if (!machine) {
        // skip, wait until all machine are loaded after SignalR connection started
        return false
      }

      return machine.lotNo === lotNo
    })
  }

  // Use the correct query key for machine options
  private getAllMachines() {
    console.log('getAllMachines')
    return this.queryClient.getQueryData(
      fetchMachineSelectOptions().queryKey
    ) as MachineSelectOptions
  }

  // Register event handlers for real-time updates
  private registerEventHandlers() {
    // Only register handlers if not already registered
    if (this._handlersRegistered) return
    this._handlersRegistered = true

    this.trackingService.handleReceiveMachineTrackingData((data) => {
      const allMachines = this.getAllMachines()
      if (allMachines) {
        this.loadAllMachineTrackingStates(allMachines, data.data)
      }
    })

    this.trackingService.handleUpdateMachineTracking((data) => {
      this.updateMachineTrackingState(data)
    })
  }

  // Initialize the store and start the SignalR connection
  startConnection(
    machineOptions: MachineSelectOptions,
    queryClient?: QueryClient
  ) {
    this.trackingService.startHubConnection()

    // listen for all machine tracking data
    this.trackingService.handleReceiveMachineTrackingData((data) => {
      this.loadAllMachineTrackingStates(machineOptions, data.data)

      // To be able to display machine status in the table in other query pages,
      // we must clear query cache for all routes using wo1210 query
      queryClient?.invalidateQueries({
        queryKey: ['wo1210'],
      })
    })

    // listen for update on machine
    this.trackingService.handleUpdateMachineTracking((data) => {
      this.updateMachineTrackingState(data)

      // Clear query cache for all routes using wo1210 query
      queryClient?.invalidateQueries({
        queryKey: ['wo1210'],
      })
    })
  }

  // Stop the SignalR connection
  stopConnection() {
    this.trackingService.stopHubConnection()
    runInAction(() => {
      this.machines.clear()
    })
  }
  loadAllMachineTrackingStates(
    allMachines: MachineSelectOptions,
    machineTrackingData: MachineTrackingModel[]
  ) {
    allMachines.forEach((mc) => {
      let machine = machineTrackingData.find((m) => m.machineId === mc.id)

      if (!machine) {
        machine = {
          machineId: mc.id,
          workOrderScheduleId: null,
          startTime: null,
          estimatedCompletionTime: null,
          status: MachineStatus.IDLE,
          lotNo: null,
          itemId: null,
          outOfServiceReason: null,
          notes: null,
          statusColor: '',
          statusLabel: '',
        }
      }
      const parsedData = MachineTrackingModelSchema.parse(machine)
      const duration = this.calculateMachineDurationUntilNow(
        parsedData.startTime
      )

      this.machines.set(machine.machineId, {
        code: mc.code,
        group: mc.group,
        capacityPerHour: mc.capacityPerHour,
        durationInMinutes: duration,
        formattedDuration: this.formatDuration(duration, parsedData.status),
        ...parsedData,
      })
    })
  }

  updateMachineTrackingState(data: MachineTrackingModel) {
    const machine = this.machines.get(data.machineId)
    if (!machine) {
      // Try to find machine info from allMachines (from query cache)
      const allMachines = this.getAllMachines()
      const mc = allMachines?.find((m) => m.id === data.machineId)
      if (!mc) {
        console.warn(
          `Machine info for ID ${data.machineId} not found in machine options.`
        )
        return
      }
      // If not found, add a new machine entry with default values
      const parsedData = MachineTrackingModelSchema.parse(data)
      const duration = this.calculateMachineDurationUntilNow(
        parsedData.startTime
      )
      this.machines.set(data.machineId, {
        code: mc.code,
        group: mc.group,
        capacityPerHour: mc.capacityPerHour,
        durationInMinutes: duration,
        formattedDuration: this.formatDuration(duration, parsedData.status),
        ...parsedData,
      })
      return
    }
    const parsedData = MachineTrackingModelSchema.parse(data)
    const duration = this.calculateMachineDurationUntilNow(parsedData.startTime)
    this.machines.set(data.machineId, {
      code: machine.code,
      group: machine.group,
      capacityPerHour: machine.capacityPerHour,
      durationInMinutes: duration,
      formattedDuration: this.formatDuration(duration, parsedData.status),
      ...parsedData,
    })
  }

  *startJob(machineId: number, workOrderScheduleId: number) {
    const machine = this.getMachine(machineId)
    this.setMachineIsLoading(machine, true)

    yield this.trackingService
      .startJob(machine.machineId, workOrderScheduleId)
      .then(() => {
        // Update duration immediately to show it on the UI
        this.updateDurations()
      })
      .catch((err) => {
        this.error = err
        this.setMachineIsLoading(machine, false)
      })
  }

  *completeJob(machineId: number) {
    const machine = this.getMachine(machineId)
    this.setMachineIsLoading(machine, true)

    yield this.trackingService.completeJob(machine.machineId).catch((err) => {
      this.error = err
      this.setMachineIsLoading(machine, false)
    })
  }

  *offService(machineId: number, reason: string) {
    const machine = this.getMachine(machineId)
    this.setMachineIsLoading(machine, true)

    yield this.trackingService
      .offService(machine.machineId, reason)
      .catch((err) => {
        this.error = err
        this.setMachineIsLoading(machine, false)
      })
  }

  *returnService(machineId: number) {
    const machine = this.getMachine(machineId)
    this.setMachineIsLoading(machine, true)

    yield this.trackingService.returnService(machine.machineId).catch((err) => {
      this.error = err
      this.setMachineIsLoading(machine, false)
    })
  }

  setMachineIsLoading(machine: MachineState, value: boolean) {
    this.machines.set(machine.machineId, {
      ...machine,
      isLoading: value,
    })
  }

  // Fetch all machine tracking instances
  async fetchMachines() {
    this.error = null
  }

  // Get a specific machine by ID
  getMachine(machineId: number) {
    const mc = this.machines.get(machineId)
    if (!mc) {
      throw new Error(`Machine with ID ${machineId} not found`)
    }
    return mc
  }

  /**
   * Updates the duration of all machines in the store.
   * For each machine with a start time, calculates the duration in minutes
   * since the start time until now. The duration is stored both as raw minutes
   * and as a formatted string.
   *
   * If a machine has no start time, its duration is set to 0.
   *
   * This method mutates the machines in the store directly by:
   * - Calculating duration in minutes
   * - Updating the durationInMinutes property
   * - Updating the formattedDuration property using formatDuration()
   */
  updateDurations() {
    this.machines.forEach((machine) => {
      const duration = this.calculateMachineDurationUntilNow(machine.startTime)

      this.machines.set(machine.machineId, {
        ...machine,
        durationInMinutes: duration,
        formattedDuration: this.formatDuration(duration, machine.status),
      })
    })
  }

  // Calculate the duration in minutes from the start time until now
  // If the start time is null, return 0
  calculateMachineDurationUntilNow(startTime: Date | null): number {
    if (!startTime) {
      return 0
    }
    const durationInMinutes = Math.floor(
      (Date.now() - startTime.getTime()) / 1000 / 60
    )
    return durationInMinutes
  }

  // Format duration in human-readable format
  formatDuration(minuteDuration: number, status?: number) {
    // Return empty string if not running (for idle machines)
    if (status !== undefined && status !== MachineStatus.RUNNING) {
      return ''
    }

    const days = Math.floor(minuteDuration / (60 * 24))
    const hours = Math.floor(minuteDuration / 60) % 24 // Adjusted to avoid double-counting days
    const minutes = minuteDuration % 60

    // Handle the case when duration is 0
    if (days === 0 && hours === 0 && minutes === 0) {
      return '0 นาที'
    }

    return (
      (days > 0 ? `${days} วัน ` : '') +
      (hours > 0 ? `${hours} ชม. ` : '') +
      (minutes > 0 ? `${minutes} นาที` : '')
    )
  }
}
